import { useEffect } from "react";
import { AddTaskOutlined } from "@mui/icons-material";
import { FormActions } from "../../components/common/FormActions";
import { IFormSection } from "../../types/common";
import {
  API_CHARTS_OF_ACCOUNTS,
  API_CHARTS_OF_ACCOUNTS_PARENT_ACCOUNTS,
  API_SYSTEM_WALLETS,
  API_SYSTEM_WALLETS_CHART_ASSIGNMENT,
} from "../../utils/apis";
import {
  createModalFormAction,
  createViewEdit,
  formatCurrency,
  formatDateTime,
  getForm,
} from "../../utils/helpers";
import { URL_SYSTEM_WALLETS } from "../../utils/urls";
import useGet from "../../hooks/common/useGet";
import { ASSIGN_TO_COA } from "../../utils/constants";

const url = URL_SYSTEM_WALLETS;

export const useSystemWallets = () => {
  const tableColumns = () => [
    {
      id: "wallet_type",
      header: "Wallet Type",
      filter: {},
    },
    {
      id: "wallet_name",
      header: "Wallet Name",
      filter: {},
    },
    {
      id: "phone_number",
      header: "Phone Number",
      filter: {},
    },
    {
      id: "description",
      header: "Description",
      filter: {},
    },
    {
      id: "wallet_balance",
      header: "Wallet Balance",
      filter: {},
    },
    {
      id: "created_at",
      header: "Created At",
      filter: {},
    },
    {
      id: "updated_at",
      header: "Updated At",
      filter: {},
    },
    {
      id: "user",
      header: "User",
      filter: {},
    },
    {
      id: "actions",
      header: "Actions",
    },
  ];

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        created_at: formatDateTime(item?.created_at),
        updated_at: formatDateTime(item?.updated_at),
        wallet_balance: formatCurrency(item?.wallet_balance),
        actions: (
          <FormActions
            actions={[
              ...createViewEdit(url, item?.id),
              // {
              //   label: "Assign To COA",
              //   custom: true,
              //   modalTitle: "Assign Wallet to Chart of Accounts",
              //   modalOkText: "Assign",
              //   modalOkType: "primary" as const,
              //   modalCancelText: "Cancel",
              //   modalWidth: 700,
              //   icon: AddTaskOutlined,
              //   modalContent: (setData: (data: any) => void) => (
              //     <AssignWalletToCOAForm
              //       walletId={item?.id}
              //       walletName={item?.wallet_name}
              //       chartsOfAccounts={chartsOfAccounts}
              //       chartsOfAccountsParentAccounts={
              //         chartsOfAccountsParentAccounts
              //       }
              //       chartsOfAccountsLoading={chartsOfAccountsLoading}
              //       chartsOfAccountsParentAccountsLoading={
              //         chartsOfAccountsParentAccountsLoading
              //       }
              //       setData={setData}
              //     />
              //   ),
              //   onModalOk: (data: any) => handleAssignToCoa(item?.id, data),
              // },
              createModalFormAction(
                ASSIGN_TO_COA,
                "Assign To COA",
                AddTaskOutlined,
                { wallet_id: item?.id },
              ),
            ]}
          />
        ),
      };
    }),
  ];

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "System Wallet Details",
          fields: {
            wallet_type: item?.wallet_type,
            wallet_name: item?.wallet_name,
            phone_number: item?.phone_number,
            description: item?.description,
            wallet_balance: item?.wallet_balance,
            created_at: item?.created_at,
            updated_at: item?.updated_at,
            user: item?.user,
          },
        },
      ],
    };
  };

  const formRoute = getForm(url);

  return {
    tableColumns,
    tableData,
    viewData,
    formRoute,
  };
};

export const useAssignToCoaForm = ({ form, updateFormValue }: any = {}) => {
  const modalWalletId = form?.wallet_id;
  const assignmentType = form?.assignmentType;

  const {
    data: chartsOfAccounts,
    loading: chartsOfAccountsLoading,
    loadData: loadChartsOfAccounts,
  } = useGet();

  const {
    data: chartsOfAccountsParentAccounts,
    loading: chartsOfAccountsParentAccountsLoading,
    loadData: loadChartsOfAccountsParentAccounts,
  } = useGet();

  useEffect(() => {
    loadChartsOfAccounts({ api: API_CHARTS_OF_ACCOUNTS });
  }, []);

  useEffect(() => {
    if (assignmentType === "new") {
      loadChartsOfAccountsParentAccounts({
        api: API_CHARTS_OF_ACCOUNTS_PARENT_ACCOUNTS,
      });
    }
  }, [assignmentType]);

  useEffect(() => {
    if (modalWalletId && updateFormValue) {
      updateFormValue("wallet_id", modalWalletId);
    }
  }, [modalWalletId, updateFormValue]);

  const coaOptions =
    chartsOfAccounts?.results?.map((coa: any) => ({
      id: coa.id,
      name: coa.account_name || coa.name,
    })) || [];

  const parentAccountOptions =
    chartsOfAccountsParentAccounts?.parents?.map((parent: any) => ({
      id: parent.id,
      name: parent.name || parent.account_name,
    })) || [];

  const accountTypeOptions = [
    { id: "ASSETS", name: "Assets" },
    { id: "EQUITY", name: "Equity" },
    { id: "REVENUE", name: "Revenue" },
    { id: "EXPENSE", name: "Expense" },
    { id: "LIABILITIES", name: "Liabilities" },
    { id: "INCOME", name: "Income" },
  ];

  const accountCategoryOptions = [
    { id: "ROOT", name: "Root" },
    { id: "HEADER", name: "Header" },
    { id: "DETAIL", name: "Detail" },
  ];

  const levelOptions = [
    { id: 1, name: "Level 1" },
    { id: 2, name: "Level 2" },
    { id: 3, name: "Level 3" },
  ];

  const sections: IFormSection[] = [
    {
      title: "Assign Wallet to Chart of Accounts",
      fields: [
        {
          field_name: "wallet_id",
          label: "Wallet ID",
          type: "text",
          props: {
            required: false,
            disabled: true,
          },
          visible: false,
        },
        {
          field_name: "assignmentType",
          label: "Assignment Type",
          type: "select",
          props: {
            placeholder: "Select assignment type",
            required: true,
          },
          options_data: [
            { id: "existing", name: "Assign wallet to existing COA" },
            { id: "new", name: "Assign to new COA" },
          ],
        },
        {
          field_name: "chart_of_account_id",
          label: "Select Chart of Account",
          type: "select",
          props: {
            placeholder: chartsOfAccountsLoading
              ? "Loading..."
              : "Select chart of account",
            required: true,
          },
          options_data: coaOptions,
          visible: assignmentType === "existing",
        },
        {
          field_name: "parent_account",
          label: "Parent Account",
          type: "select",
          props: {
            placeholder: chartsOfAccountsParentAccountsLoading
              ? "Loading..."
              : "Select parent account",
            required: true,
          },
          options_data: parentAccountOptions,
          visible: assignmentType === "new",
        },
        {
          field_name: "account_type",
          label: "Account Type",
          type: "select",
          props: {
            placeholder: "Select account type",
            required: true,
          },
          options_data: accountTypeOptions,
          visible: assignmentType === "new",
        },
        {
          field_name: "account_category",
          label: "Account Category",
          type: "select",
          props: {
            placeholder: "Select account category",
            required: true,
          },
          options_data: accountCategoryOptions,
          visible: assignmentType === "new",
        },
        {
          field_name: "level",
          label: "Level",
          type: "select",
          props: {
            placeholder: "Select level",
            required: true,
          },
          options_data: levelOptions,
          visible: assignmentType === "new",
        },
        {
          field_name: "identifier",
          label: "Identifier",
          type: "text",
          props: {
            placeholder: "Enter identifier",
            required: true,
          },
          visible: assignmentType === "new",
        },
      ],
    },
  ];

  return {
    sections,
  };
};

// const AssignWalletToCOAForm: React.FC<{
//   walletId: string;
//   walletName: string;
//   chartsOfAccounts: any;
//   chartsOfAccountsParentAccounts: any;
//   chartsOfAccountsLoading: boolean;
//   chartsOfAccountsParentAccountsLoading: boolean;
//   setData: (data: any) => void;
// }> = ({
//   walletId,
//   walletName,
//   chartsOfAccounts,
//   chartsOfAccountsParentAccounts,
//   chartsOfAccountsLoading,
//   chartsOfAccountsParentAccountsLoading,
//   setData,
// }) => {
//   const [assignmentType, setAssignmentType] = useState<string>("");
//   const [formData, setFormData] = useState<any>({
//     wallet_id: walletId,
//     assignmentType: "",
//   });
//   const [errors, setErrors] = useState<any>({});

//   const updateFormData = (field: string, value: any) => {
//     const updatedData = { ...formData, [field]: value };
//     setFormData(updatedData);
//     setData(updatedData);
//     // Clear error for this field when user updates it
//     if (errors[field]) {
//       setErrors({ ...errors, [field]: "" });
//     }
//   };

//   const accountTypeOptions = [
//     { label: "Assets", value: "ASSETS" },
//     { label: "Equity", value: "EQUITY" },
//     { label: "Revenue", value: "REVENUE" },
//     { label: "Expense", value: "EXPENSE" },
//     { label: "Liabilities", value: "LIABILITIES" },
//     { label: "Income", value: "INCOME" },
//   ];

//   const accountCategoryOptions = [
//     { label: "Root", value: "ROOT" },
//     { label: "Header", value: "HEADER" },
//     { label: "Detail", value: "DETAIL" },
//   ];

//   const levelOptions = [
//     { label: "Level 1", value: 1 },
//     { label: "Level 2", value: 2 },
//     { label: "Level 3", value: 3 },
//   ];

//   const coaOptions =
//     chartsOfAccounts?.results?.map((coa: any) => ({
//       label: coa.account_name || coa.name,
//       value: coa.id,
//     })) || [];

//   const parentAccountOptions =
//     chartsOfAccountsParentAccounts?.parents?.map((parent: any) => ({
//       label: parent.name || parent.account_name,
//       value: parent.id,
//     })) || [];

//   return (
//     <div style={{ padding: "10px 0" }}>
//       <p style={{ marginBottom: "16px" }}>
//         Assign wallet <strong>{walletName}</strong> to a Chart of Accounts.
//       </p>

//       {/* Assignment Type Selection */}
//       <div style={{ marginBottom: "16px" }}>
//         <label
//           style={{
//             display: "block",
//             marginBottom: "8px",
//             fontWeight: 500,
//           }}
//         >
//           Assignment Type <span style={{ color: "red" }}>*</span>
//         </label>
//         <Select
//           placeholder="Select assignment type"
//           style={{ width: "100%" }}
//           value={assignmentType || undefined}
//           onChange={(value) => {
//             setAssignmentType(value);
//             updateFormData("assignmentType", value);
//           }}
//           options={[
//             { label: "Assign wallet to existing COA", value: "existing" },
//             { label: "Assign to new COA", value: "new" },
//           ]}
//         />
//       </div>

//       {/* Option 1: Assign to Existing COA */}
//       {assignmentType === "existing" && (
//         <div style={{ marginBottom: "16px" }}>
//           <label
//             style={{
//               display: "block",
//               marginBottom: "8px",
//               fontWeight: 500,
//             }}
//           >
//             Select Chart of Account <span style={{ color: "red" }}>*</span>
//           </label>
//           <SelectInput
//             name="chart_of_account_id"
//             value={formData.chart_of_account_id || null}
//             onChange={(value) => updateFormData("chart_of_account_id", value)}
//             options={coaOptions}
//             placeholder={
//               chartsOfAccountsLoading ? "Loading..." : "Select chart of account"
//             }
//             loading={chartsOfAccountsLoading}
//           />
//           {errors.chart_of_account_id && (
//             <span style={{ color: "red", fontSize: "12px" }}>
//               {errors.chart_of_account_id}
//             </span>
//           )}
//         </div>
//       )}

//       {/* Option 2: Create New COA */}
//       {assignmentType === "new" && (
//         <div>
//           {/* Row 1: Parent Account & Account Type */}
//           <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
//             <div style={{ flex: 1 }}>
//               <label
//                 style={{
//                   display: "block",
//                   marginBottom: "8px",
//                   fontWeight: 500,
//                 }}
//               >
//                 Parent Account <span style={{ color: "red" }}>*</span>
//               </label>
//               <SelectInput
//                 name="parent_account"
//                 value={formData.parent_account || null}
//                 onChange={(value) => updateFormData("parent_account", value)}
//                 options={parentAccountOptions}
//                 placeholder={
//                   chartsOfAccountsParentAccountsLoading
//                     ? "Loading..."
//                     : "Select parent account"
//                 }
//                 loading={chartsOfAccountsParentAccountsLoading}
//               />
//               {errors.parent_account && (
//                 <span style={{ color: "red", fontSize: "12px" }}>
//                   {errors.parent_account}
//                 </span>
//               )}
//             </div>

//             <div style={{ flex: 1 }}>
//               <label
//                 style={{
//                   display: "block",
//                   marginBottom: "8px",
//                   fontWeight: 500,
//                 }}
//               >
//                 Account Type <span style={{ color: "red" }}>*</span>
//               </label>
//               <SelectInput
//                 name="account_type"
//                 value={formData.account_type || null}
//                 onChange={(value) => updateFormData("account_type", value)}
//                 options={accountTypeOptions}
//                 placeholder="Select account type"
//               />
//               {errors.account_type && (
//                 <span style={{ color: "red", fontSize: "12px" }}>
//                   {errors.account_type}
//                 </span>
//               )}
//             </div>
//           </div>

//           {/* Row 2: Account Category & Level */}
//           <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
//             <div style={{ flex: 1 }}>
//               <label
//                 style={{
//                   display: "block",
//                   marginBottom: "8px",
//                   fontWeight: 500,
//                 }}
//               >
//                 Account Category <span style={{ color: "red" }}>*</span>
//               </label>
//               <SelectInput
//                 name="account_category"
//                 value={formData.account_category || null}
//                 onChange={(value) => updateFormData("account_category", value)}
//                 options={accountCategoryOptions}
//                 placeholder="Select account category"
//               />
//               {errors.account_category && (
//                 <span style={{ color: "red", fontSize: "12px" }}>
//                   {errors.account_category}
//                 </span>
//               )}
//             </div>

//             <div style={{ flex: 1 }}>
//               <label
//                 style={{
//                   display: "block",
//                   marginBottom: "8px",
//                   fontWeight: 500,
//                 }}
//               >
//                 Level <span style={{ color: "red" }}>*</span>
//               </label>
//               <SelectInput
//                 name="level"
//                 value={formData.level || null}
//                 onChange={(value) => updateFormData("level", value)}
//                 options={levelOptions}
//                 placeholder="Select level"
//               />
//               {errors.level && (
//                 <span style={{ color: "red", fontSize: "12px" }}>
//                   {errors.level}
//                 </span>
//               )}
//             </div>
//           </div>

//           {/* Row 3: Identifier (full width) */}
//           <div style={{ marginBottom: "16px" }}>
//             <label
//               style={{
//                 display: "block",
//                 marginBottom: "8px",
//                 fontWeight: 500,
//               }}
//             >
//               Identifier <span style={{ color: "red" }}>*</span>
//             </label>
//             <Input
//               placeholder="Enter identifier"
//               value={formData.identifier || ""}
//               onChange={(e) => updateFormData("identifier", e.target.value)}
//               status={errors.identifier ? "error" : ""}
//             />
//             {errors.identifier && (
//               <span style={{ color: "red", fontSize: "12px" }}>
//                 {errors.identifier}
//               </span>
//             )}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

export const useSystemWalletsForm = () => {
  const sections: IFormSection[] = [
    {
      title: "System Wallet Details",
      fields: [
        {
          field_name: "wallet_type",
          label: "Wallet Type",
          type: "select",
          props: {
            required: true,
          },
          options_data: [
            {
              id: "COMMISSION",
              name: "Commission",
            },
            {
              id: "ESCROW",
              name: "Escrow",
            },
            {
              id: "EXPENSES",
              name: "Expenses",
            },
            {
              id: "SUSPENSE",
              name: "Suspense",
            },
          ],
        },
        {
          field_name: "wallet_name",
          label: "Wallet Name",
          type: "text",
          props: {
            required: true,
          },
        },
        {
          field_name: "description",
          label: "Description",
          type: "textarea",
          props: {
            required: true,
          },
        },
        {
          field_name: "phone_number",
          label: "Phone Number",
          type: "tel",
          props: {
            required: true,
          },
        },
        {
          field_name: "wallet_balance",
          label: "Wallet Balance",
          type: "number",
          props: {
            required: true,
          },
        },
      ],
    },
  ];

  return {
    sections,
  };
};

export const submitAssignToCaoForm = (
  payload: any,
  id: string,
  _: any,
  reloadData: any,
) => {
  const { assignmentType, wallet_id, ...rest } = payload;

  let finalPayload: any = {
    wallet_id,
  };

  if (assignmentType === "existing") {
    finalPayload.chart_of_account_id = rest.chart_of_account_id;
  } else if (assignmentType === "new") {
    finalPayload = {
      ...finalPayload,
      parent_account: rest.parent_account,
      account_type: rest.account_type,
      account_category: rest.account_category,
      identifier: rest.identifier,
      level: rest.level,
    };
  }

  return {
    api: API_SYSTEM_WALLETS_CHART_ASSIGNMENT,
    method: "POST",
    params: finalPayload,
    onSuccess: () => {
      if (reloadData) reloadData({});
    },
  };
};

export const submitSystemWallets = (payload: any, id: string) => {
  if (id) {
    return {
      id,
      api: `${API_SYSTEM_WALLETS}`,
      params: {
        ...payload,
      },
    };
  }
  return {
    api: `${API_SYSTEM_WALLETS}`,
    params: {
      ...payload,
    },
  };
};
